const DB_NAME = 'bari-drafts';
const DB_VERSION = 1;

const STORES = {
  CHECKLIST_DRAFTS: 'checklist-drafts',
  CHECKLIST_FILES: 'checklist-files',
  WIZARD_DRAFTS: 'wizard-drafts',
  WIZARD_FILES: 'wizard-files',
} as const;

// === Types ===

export interface ChecklistDraftEntry {
  inspectionId: string;
  checklistId: string;
  formValues: Record<string, any>;
  savedAt: string;
}

export interface DraftFileEntry {
  key: string;
  blob: Blob;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface WizardDraftEntry {
  inspectionItemId: string;
  wizardData: any;
  currentStep: number;
  savedAt: string;
}

// === Database ===

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORES.CHECKLIST_DRAFTS)) {
        db.createObjectStore(STORES.CHECKLIST_DRAFTS, { keyPath: 'inspectionId' });
      }
      if (!db.objectStoreNames.contains(STORES.CHECKLIST_FILES)) {
        db.createObjectStore(STORES.CHECKLIST_FILES, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.WIZARD_DRAFTS)) {
        db.createObjectStore(STORES.WIZARD_DRAFTS, { keyPath: 'inspectionItemId' });
      }
      if (!db.objectStoreNames.contains(STORES.WIZARD_FILES)) {
        db.createObjectStore(STORES.WIZARD_FILES, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function doTransaction<T>(
  storeName: string,
  mode: IDBTransactionMode,
  callback: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        const request = callback(store);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

function deleteByPrefix(storeName: string, prefix: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            if (String(cursor.key).startsWith(prefix)) {
              cursor.delete();
            }
            cursor.continue();
          }
        };
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

function getAllByPrefix<T>(storeName: string, prefix: string): Promise<T[]> {
  return openDB().then(
    (db) =>
      new Promise<T[]>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const results: T[] = [];
        const request = store.openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            if (String(cursor.key).startsWith(prefix)) {
              results.push(cursor.value as T);
            }
            cursor.continue();
          }
        };
        tx.oncomplete = () => {
          db.close();
          resolve(results);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

// === Public API ===

export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

export const draftStorage = {
  // === Checklist ===

  async saveChecklistDraft(
    inspectionId: string,
    checklistId: string,
    formValues: Record<string, any>,
  ): Promise<void> {
    const entry: ChecklistDraftEntry = {
      inspectionId,
      checklistId,
      formValues,
      savedAt: new Date().toISOString(),
    };
    await doTransaction(STORES.CHECKLIST_DRAFTS, 'readwrite', (store) => store.put(entry));
  },

  async getChecklistDraft(inspectionId: string): Promise<ChecklistDraftEntry | null> {
    const result = await doTransaction<ChecklistDraftEntry | undefined>(
      STORES.CHECKLIST_DRAFTS,
      'readonly',
      (store) => store.get(inspectionId),
    );
    return result ?? null;
  },

  async deleteChecklistDraft(inspectionId: string): Promise<void> {
    await doTransaction(STORES.CHECKLIST_DRAFTS, 'readwrite', (store) =>
      store.delete(inspectionId),
    );
    await deleteByPrefix(STORES.CHECKLIST_FILES, `${inspectionId}:`);
  },

  async saveChecklistFile(
    inspectionId: string,
    questionId: string,
    fileIndex: number,
    file: File | Blob,
    fileName: string,
  ): Promise<void> {
    const key = `${inspectionId}:${questionId}:${fileIndex}`;
    const entry: DraftFileEntry & { key: string } = {
      key,
      blob: file,
      fileName,
      fileType: file.type,
      fileSize: file.size,
    };
    await doTransaction(STORES.CHECKLIST_FILES, 'readwrite', (store) => store.put(entry));
  },

  async getChecklistFiles(inspectionId: string, questionId: string): Promise<DraftFileEntry[]> {
    return getAllByPrefix<DraftFileEntry>(
      STORES.CHECKLIST_FILES,
      `${inspectionId}:${questionId}:`,
    );
  },

  async clearChecklistFiles(inspectionId: string): Promise<void> {
    await deleteByPrefix(STORES.CHECKLIST_FILES, `${inspectionId}:`);
  },

  // === Wizard ===

  async saveWizardDraft(
    inspectionItemId: string,
    wizardData: any,
    currentStep: number,
  ): Promise<void> {
    const entry: WizardDraftEntry = {
      inspectionItemId,
      wizardData,
      currentStep,
      savedAt: new Date().toISOString(),
    };
    await doTransaction(STORES.WIZARD_DRAFTS, 'readwrite', (store) => store.put(entry));
  },

  async getWizardDraft(inspectionItemId: string): Promise<WizardDraftEntry | null> {
    const result = await doTransaction<WizardDraftEntry | undefined>(
      STORES.WIZARD_DRAFTS,
      'readonly',
      (store) => store.get(inspectionItemId),
    );
    return result ?? null;
  },

  async deleteWizardDraft(inspectionItemId: string): Promise<void> {
    await doTransaction(STORES.WIZARD_DRAFTS, 'readwrite', (store) =>
      store.delete(inspectionItemId),
    );
    await deleteByPrefix(STORES.WIZARD_FILES, `${inspectionItemId}:`);
  },

  async saveWizardFile(
    inspectionItemId: string,
    fieldName: string,
    blob: Blob,
    fileName: string,
  ): Promise<void> {
    const key = `${inspectionItemId}:${fieldName}`;
    const entry: DraftFileEntry & { key: string } = {
      key,
      blob,
      fileName,
      fileType: blob.type,
      fileSize: blob.size,
    };
    await doTransaction(STORES.WIZARD_FILES, 'readwrite', (store) => store.put(entry));
  },

  async getWizardFile(inspectionItemId: string, fieldName: string): Promise<DraftFileEntry | null> {
    const key = `${inspectionItemId}:${fieldName}`;
    const result = await doTransaction<(DraftFileEntry & { key: string }) | undefined>(
      STORES.WIZARD_FILES,
      'readonly',
      (store) => store.get(key),
    );
    return result ?? null;
  },

  async clearWizardFiles(inspectionItemId: string): Promise<void> {
    await deleteByPrefix(STORES.WIZARD_FILES, `${inspectionItemId}:`);
  },
};
